import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { get, ref } from 'firebase/database';
import { database } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { getTeamTransactions } from '../services/transactionService';
import { isTeamLeaderEmail } from '../config/commissionPlans';
import { pendingCommissionBreakdown, sortPendingRows } from '../services/commissionMath';

const dollars = (n) => `$${Math.round(n || 0).toLocaleString('en-US')}`;
const fmtDate = (s) => {
  if (!s) return 'No closing date';
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T12:00:00` : s);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : s;
};

export default function PendingCommission() {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sort, setSort] = useState('date-asc');

  useEffect(() => {
    if (!user?.uid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [deals, snap] = await Promise.all([
          getTeamTransactions(user.uid, userRole),
          get(ref(database, `users/${user.uid}/commissionSettings`)).catch(() => null),
        ]);
        // Same deal list and settings as the dashboard card.
        const mine = deals.filter(t => !t.ownerUid || t.ownerUid === user.uid);
        const res = pendingCommissionBreakdown(mine, isTeamLeaderEmail(user.email), new Date(), undefined, (snap && snap.val()) || {});
        if (!cancelled) setData(res);
      } catch (e) {
        console.error('Could not load pending commission:', e);
        if (!cancelled) setData({ rows: [], total: 0, error: true });
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid, user?.email, userRole]);

  const [key, dir] = sort.split('-');
  const rows = data ? sortPendingRows(data.rows, key, dir) : [];

  return (
    <div style={{ padding: '1rem', maxWidth: 900, margin: '0 auto', boxSizing: 'border-box' }}>
      <button type="button" onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', color: '#8C6D1F', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 14 }}>← Back to dashboard</button>
      <h1 style={{ margin: '0 0 0.25rem', color: '#1a1a1a', fontSize: '1.5rem' }}>Pending Commission</h1>
      <p style={{ margin: '0 0 1rem', color: '#7f7f7f', fontSize: '0.85rem' }}>
        Your open deals and your anticipated take-home on each, after team and eXp splits (same math as the dashboard card).
      </p>
      <div style={{ background: '#fff', border: '1px solid #d8c9a8', borderLeft: '4px solid #D4AF37', borderRadius: 8, padding: '1rem', marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div>
          <div style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8C6D1F' }}>Total</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#1a1a1a' }}>{data ? dollars(data.total) : '…'}</div>
          <div style={{ fontSize: '0.85rem', color: '#7f7f7f' }}>{data ? `${data.rows.length} open deal${data.rows.length === 1 ? '' : 's'}` : 'Loading'}</div>
        </div>
        <label style={{ fontSize: 13, color: '#1a1a1a' }}>Sort by{' '}
          <select value={sort} onChange={e => setSort(e.target.value)} style={{ padding: '6px', borderRadius: 6, border: '1px solid #b8a57a', fontSize: 14 }}>
            <option value="date-asc">Closing date (soonest)</option>
            <option value="date-desc">Closing date (latest)</option>
            <option value="commission-desc">Commission (highest)</option>
            <option value="name-asc">Name (A–Z)</option>
          </select>
        </label>
      </div>
      {data?.error && <div style={{ color: '#a33' }}>Could not load your deals. Please refresh.</div>}
      {data && !data.error && rows.length === 0 && <div style={{ color: '#7f7f7f', textAlign: 'center', padding: '1.5rem' }}>No open deals right now.</div>}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}>
        {rows.map(r => (
          <li key={r.id}>
            <Link to={`/transactions/${r.id}`} style={{ display: 'block', textDecoration: 'none', color: 'inherit', background: '#fff', border: '1px solid #d8c9a8', borderRadius: 8, padding: '0.85rem 1rem' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                  <div style={{ fontWeight: 700, color: '#1a1a1a', overflowWrap: 'anywhere' }}>{r.name}</div>
                  {r.address && r.address !== r.name && <div style={{ fontSize: 13, color: '#555', overflowWrap: 'anywhere' }}>{r.address}</div>}
                  <div style={{ fontSize: 13, color: '#7f7f7f', marginTop: 2 }}>Closing: {fmtDate(r.closingDate)}</div>
                </div>
                <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                  <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#8C6D1F' }}>{dollars(r.commission)}</div>
                  <div style={{ fontSize: 12, color: '#7f7f7f' }}>{r.gross ? `of ${dollars(r.gross)} gross` : 'Commission not set'}</div>
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
